import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  message,
} from 'antd';
import AdaptiveTable from './AdaptiveTable';
import { cartonBatchApi, type CartonBatchDTO, type CartonDTO } from '../api/kfc';
import CargoNameSelect, { cargoNameFormItemProps } from './CargoNameSelect';
import CartonDetailDrawer from './CartonDetailDrawer';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const SOURCE_LABELS: Record<string, string> = {
  MANUAL: '手动录入',
  AGENT_SYNC: '代理同步',
  LEGACY_MIGRATION: '历史数据迁移',
};

export interface CartonBatchDetailDrawerProps {
  open: boolean;
  /** 批次文档 id */
  batchDbId?: string;
  suppliers: { value: string; label: string }[];
  customers: { value: string; label: string }[];
  onClose: () => void;
  onChanged?: () => void;
}

const CartonBatchDetailDrawer: React.FC<CartonBatchDetailDrawerProps> = ({
  open,
  batchDbId,
  suppliers,
  customers,
  onClose,
  onChanged,
}) => {
  const [batch, setBatch] = useState<CartonBatchDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [cartonDrawerId, setCartonDrawerId] = useState<string | undefined>(undefined);

  const load = useCallback(() => {
    if (!batchDbId) return;
    setLoading(true);
    cartonBatchApi
      .get(batchDbId)
      .then((res) => setBatch(res.data))
      .catch(() => {
        setBatch(null);
        message.error('加载批次失败');
      })
      .finally(() => setLoading(false));
  }, [batchDbId]);

  useEffect(() => {
    if (open) {
      setEditing(false);
      load();
    } else {
      setBatch(null);
    }
  }, [open, load]);

  const labelOf = (opts: { value: string; label: string }[], id?: string) =>
    opts.find((o) => o.value === id)?.label || id || '-';

  const startEdit = () => {
    if (!batch) return;
    form.setFieldsValue({
      pieceCount: batch.pieceCount,
      fromSupplierId: batch.fromSupplierId,
      belongClientId: batch.belongClientId,
      cargoName: batch.cargoName,
      batchRemark: batch.batchRemark,
    });
    setEditing(true);
  };

  const save = () => {
    if (!batch?.id) return;
    form.validateFields().then((values) => {
      setSaving(true);
      cartonBatchApi
        .update(batch.id!, {
          pieceCount: values.pieceCount,
          fromSupplierId: values.fromSupplierId,
          belongClientId: values.belongClientId,
          cargoName: values.cargoName ?? '',
          batchRemark: values.batchRemark ?? '',
        })
        .then(() => {
          message.success('已保存');
          setEditing(false);
          load();
          onChanged?.();
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'))
        .finally(() => setSaving(false));
    });
  };

  const remove = () => {
    if (!batch?.id) return;
    cartonBatchApi
      .remove(batch.id)
      .then(() => {
        message.success('已删除批次');
        onChanged?.();
        onClose();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const cartons = batch?.cartons || [];
  const canDelete = !!batch && !batch.shipmentId && (batch.materializedCount ?? 0) === 0;

  return (
    <Drawer
      title={batch ? `批次 ${batch.batchId}` : '批次详情'}
      open={open}
      onClose={onClose}
      width={800}
      extra={
        batch && (
          <Space>
            {editing ? (
              <>
                <Button onClick={() => setEditing(false)}>取消</Button>
                <Button type="primary" loading={saving} onClick={save}>保存</Button>
              </>
            ) : (
              <Button onClick={startEdit}>编辑</Button>
            )}
            <Popconfirm
              title="确认删除该批次？"
              disabled={!canDelete}
              onConfirm={remove}
            >
              <Button danger disabled={!canDelete} title={canDelete ? undefined : '已绑定运单或已有单箱的批次不可删除'}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    >
      <Spin spinning={loading}>
        {batch && !editing && (
          <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="批次标识">{batch.batchId}</Descriptions.Item>
            <Descriptions.Item label="件数">{batch.pieceCount ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="已生成单箱">{batch.materializedCount ?? 0}</Descriptions.Item>
            <Descriptions.Item label="运单号">{batch.shipmentId || '-'}</Descriptions.Item>
            <Descriptions.Item label="供应商">{labelOf(suppliers, batch.fromSupplierId)}</Descriptions.Item>
            <Descriptions.Item label="客户">{labelOf(customers, batch.belongClientId)}</Descriptions.Item>
            <Descriptions.Item label="来源">{SOURCE_LABELS[batch.source || ''] || batch.source || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {batch.createTimestamp ? new Date(batch.createTimestamp).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="货物品名" span={2}>{batch.cargoName || '-'}</Descriptions.Item>
            <Descriptions.Item label="批次备注" span={2}>{batch.batchRemark || '-'}</Descriptions.Item>
          </Descriptions>
        )}
        {batch && editing && (
          <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
            <Space style={{ display: 'flex', width: '100%' }} align="start">
              <Form.Item name="pieceCount" label="件数" rules={[{ required: true, message: '请填写件数' }]} style={{ flex: 1 }}>
                <InputNumber min={Math.max(1, batch.materializedCount ?? 0)} max={500} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="fromSupplierId" label="供应商" style={{ flex: 2 }}>
                <Select showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="belongClientId" label="客户" style={{ flex: 2 }}>
                <Select showSearch optionFilterProp="label" options={customers} style={{ width: '100%' }} disabled={!!batch.shipmentId} />
              </Form.Item>
            </Space>
            <Form.Item name="cargoName" label="货物品名" {...cargoNameFormItemProps}>
              <CargoNameSelect />
            </Form.Item>
            <Form.Item name="batchRemark" label="批次备注">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        )}
        <h4 style={{ marginBottom: 8 }}>单箱（{cartons.length}）</h4>
        <AdaptiveTable<CartonDTO>
          size="small"
          rowKey="id"
          dataSource={cartons}
          pagination={false}
          locale={{ emptyText: '未生成单箱（同步代理入仓数据后匹配生成）' }}
          onRow={(record) => ({
            onClick: () => record.id && setCartonDrawerId(record.id),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: '货物标识', dataIndex: 'cartonId', ellipsis: true },
            { title: '状态', dataIndex: 'state', width: 150, render: (s) => STATE_LABELS[s || ''] || s || '-' },
            {
              title: '长×宽×高(cm)',
              width: 150,
              render: (_, r) => (r.lengthCm != null ? `${r.lengthCm}×${r.widthCm ?? '-'}×${r.heightCm ?? '-'}` : '-'),
            },
            { title: '毛重(kg)', dataIndex: 'measuredWeightKg', width: 100, render: (v) => (v != null ? v : '-') },
            { title: '运单号', dataIndex: 'shipmentId', width: 140, render: (v) => v || '-' },
          ]}
        />
      </Spin>
      <CartonDetailDrawer
        open={!!cartonDrawerId}
        cartonId={cartonDrawerId}
        onClose={() => setCartonDrawerId(undefined)}
        onSaved={() => load()}
      />
    </Drawer>
  );
};

export default CartonBatchDetailDrawer;
